/**
 * idempotency.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SKPLATFORM-UICC-001 CH-U4 — the console sends `X-Idempotency-Key`.
 *
 * skplatform-api (SKPLATFORM-CC-002 CH-5) marks a set of routes `required`.
 * Today it runs in `warn` mode and only logs a missing key; when it flips to
 * `reject` the same request becomes a 400. So this has to land BEFORE the flip
 * — that ordering is the whole reason CH-U4 is first in the UI CR's sequencing.
 *
 * ── The one rule that is easy to get backwards ────────────────────────────
 * A key identifies a LOGICAL SUBMIT, not a request:
 *
 *   · An AUTOMATIC retry (the 401-refresh replay, a timeout, a 5xx) must carry
 *     the SAME key. That is what makes retrying safe — the server recognises
 *     the key and returns the original response instead of running the effect
 *     a second time.
 *   · A DELIBERATE re-submit — the person pressing the button again after a
 *     settled failure — must carry a FRESH key, or it replays the old failure's
 *     response and silently does nothing.
 *
 * Both behaviours fall out of one decision: mint ONLY IF ABSENT, on the axios
 * config. A replay reuses the same config object, so it keeps its key. A new
 * submit builds a new config, so it gets a new one. Nothing has to track
 * "submits" explicitly, which is what makes this correct rather than merely
 * careful.
 *
 * The double-click guard on submit buttons stays, but it is UX polish. This is
 * the correctness mechanism.
 */

export interface RequiredRoute {
  method: string;
  /** Express-style pattern; `:param` matches exactly one non-empty segment. */
  pattern: string;
}

/**
 * Mirror of skplatform-api's `IDEMPOTENT_REQUIRED_ROUTES`
 * (src/middleware/idempotency-routes.ts).
 *
 * **Keep these two lists in step.** A route the API requires and this table
 * omits is a 400 the day enforcement flips; a route this table adds that the
 * API does not require is a harmless-but-confusing header. The API is the
 * authority — when they disagree, change this file.
 *
 * Only `required` routes are listed. Routes the API treats as `optional` are
 * deliberately absent: sending a key there costs a round of replay-store writes
 * and buys nothing the API asked for.
 */
export const IDEMPOTENT_REQUIRED_ROUTES: readonly RequiredRoute[] = [
  { method: 'POST', pattern: '/v1/orgs' },
  { method: 'POST', pattern: '/v1/auth/signup' },
  { method: 'DELETE', pattern: '/v1/orgs/:id/members/:user_id' },
  { method: 'POST', pattern: '/v1/orgs/:id/invitations' },
  { method: 'POST', pattern: '/v1/auth/accept-invitation' },
  { method: 'POST', pattern: '/v1/orgs/:id/members/:user_id/roles' },
  { method: 'DELETE', pattern: '/v1/orgs/:id/members/:user_id/roles/:role_id' },
  { method: 'POST', pattern: '/v1/orgs/:id/subscription' },
  { method: 'POST', pattern: '/v1/orgs/:id/subscriptions/:sub_id/change-plan' },
  { method: 'POST', pattern: '/v1/orgs/:id/external-invites' },
];

export const IDEMPOTENCY_HEADER = 'X-Idempotency-Key';

/**
 * A v4 UUID.
 *
 * `crypto.randomUUID` is used when present (web, and Hermes with a polyfill)
 * and a Math.random construction is the fallback — React Native has no global
 * `crypto` on every platform/version, and an exception thrown from a request
 * interceptor would break the request for a reason unrelated to the screen.
 *
 * The fallback's randomness is weaker than a CSPRNG's, which is fine: this
 * value is a de-duplication token scoped to one org and one route, never a
 * secret and never an authorisation.
 */
export function newIdempotencyKey(): string {
  const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** The path portion of an absolute or relative URL, without query or hash. */
function pathOf(url: string): string {
  // Strip scheme+host by hand rather than with `new URL`: the console runs on
  // React Native too, where the URL polyfill has historically been partial.
  const withoutOrigin = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  return withoutOrigin.split('?')[0].split('#')[0];
}

/**
 * True when `pattern` matches `path`, with `:param` binding exactly one
 * non-empty segment.
 *
 * Compared segment by segment rather than by compiling the pattern to a RegExp:
 * a naive `:param` → `[^/]+` translation leaves dots unescaped and the anchors
 * easy to forget, and the consequence is a pattern matching more routes than it
 * names — which in reject mode is a 400 on a screen nobody changed.
 */
function matchPattern(pattern: string, path: string): boolean {
  const split = (s: string) => s.replace(/\/+$/, '').split('/');
  const p = split(pattern);
  const a = split(path);
  if (p.length !== a.length) return false;

  for (let i = 0; i < p.length; i += 1) {
    const seg = p[i];
    const actual = a[i];
    // An empty segment (from '//') must never satisfy a parameter.
    if (i > 0 && actual.length === 0) return false;
    if (seg.startsWith(':')) continue;
    if (seg !== actual) return false;
  }
  return true;
}

/** True when this (method, url) is a route skplatform-api marks `required`. */
export function matchesRequiredRoute(method: string, url: string): boolean {
  const m = (method || '').toUpperCase();
  const path = pathOf(url || '');
  return IDEMPOTENT_REQUIRED_ROUTES.some(
    (r) => r.method === m && matchPattern(r.pattern, path),
  );
}

/**
 * Attach a key to an axios request config, if the route needs one and the
 * config does not already carry one.
 *
 * Mutates `config` deliberately — axios request interceptors work on the
 * config object, and the "already carries one" check reading back from that
 * same object is exactly what makes a replay reuse its key.
 */
export function attachIdempotencyKey(config: {
  method?: string;
  url?: string;
  headers?: Record<string, any>;
}): void {
  if (!config.url || !config.method) return;
  if (!matchesRequiredRoute(config.method, config.url)) return;

  if (!config.headers) config.headers = {};
  // Never overwrite: this is both the replay path and the escape hatch for a
  // caller pinning its own key across a multi-step submit.
  if (config.headers[IDEMPOTENCY_HEADER]) return;

  config.headers[IDEMPOTENCY_HEADER] = newIdempotencyKey();
}
